import { map, find } from 'lodash';
import moment from 'moment';
import * as constants from 'Common/constants/workbench';
import { LOAN_APP_STATUS_TYPES } from 'Common/constants/loanAppStatusTypes';
import { typelessEqual } from 'Common/utilities/math';
import { loanStatusBuilderForUI } from 'Common/mappers/options';
import { PIPELINE_TYPES_ID } from 'Common/constants/pipelineType';
import { LOAN_STATUS } from 'Common/constants/loanStatusCategory';
import { OK } from 'Common/constants/httpStatusCodes';

class workbenchDetailsCtrl {
  constructor(
    optionsService,
    contactService,
    toaster,
    loanAppSharedData,
    loanScenarioService,
    modalRenderService,
    pipelineSharedData,
    pipelineService,
    uiService,
    taskDetailService,
    automationRuleModelService,
  ) {
    'ngInject';

    this.optionsService = optionsService;
    this.contactService = contactService;
    this.toaster = toaster;
    this.loanAppSharedData = loanAppSharedData;
    this.loanScenarioService = loanScenarioService;
    this.modalRenderService = modalRenderService;
    this.pipelineSharedData = pipelineSharedData;
    this.constants = constants;
    this.pipelineService = pipelineService;
    this.uiService = uiService;
    this.taskDetailService = taskDetailService;
    this.automationRuleModelService = automationRuleModelService;
  }

  $onInit() {
    this.reasonListView = [];
    this.statusList = [];
    this.involvedParties = [];
    this.selectedParty = {
      FamilyId: '',
      FamilyFullName: '',
    };
    this.nextWorkDate = {
      Date: null,
    };
    this.isNewPipeline = this.uiService.newPipeline;
    this.getLoanStatusList();
    this.importantDateGet();
    if (this.loanStatusId) {
      this.origLoanStatusId = this.loanStatusId.toString();
    }
    if (this.orgPipelineStatusId) {
      this.origOrgPipelineStatusId = this.orgPipelineStatusId.toString();
    }
    this.getReasonList();
  }

  $onChanges(changes) {
    if (changes && changes.changeTaskDueCount) {
      this.importantDateGet();
    }
  }

  setLoanStatus() {
    this.loanStatus = null;
    if (!this.loanStatusId || !(this.statusList || []).length) {
      return;
    }

    if (this.orgPipelineStatusId && this.isNewPipeline) {
      this.loanStatus = this.statusList.find((status) =>
        typelessEqual(this.orgPipelineStatusId, status.OrgPipelineStatusID),
      );
    }
    if (!this.loanStatus) {
      this.loanStatus =
        this.statusList.find((status) =>
          typelessEqual(this.loanStatusId, status.LoanStatusID),
        ) || null;
    }
  }

  /**
   * Description: Loan status list
   * */
  getLoanStatusList() {
    this.optionsService.getLoanStatus().then((response) => {
      this.statusList = map(response.data, loanStatusBuilderForUI);
      this.setLoanStatus();
    });
  }

  /*
   * Description: Updates the loan's status
   * */
  moveToSettled() {
    if (this.selectedParty.FamilyId && this.loanId) {
      this.modalRenderService
        .openMovedToSettledModal({
          size: 'modalLinkLoanToPipeline',
          familyID: this.selectedParty.FamilyId,
          loanId: this.loanId,
          loanScenarioId: this.loanScenarioId,
        })
        .then(() => {
          this.toaster.pop('success', 'Loan Status successfully updated!');
        });
    }
  }

  updateLoanStatus() {
    if (!this.loanStatus) {
      return;
    }

    this.loanStatusId = this.loanStatus.LoanStatusID;
    this.orgPipelineStatusId = this.loanStatus.OrgPipelineStatusID;
    if (
      typelessEqual(
        this.loanStatus.LoanStatusID,
        LOAN_APP_STATUS_TYPES.NOT_PROCEEDED,
      )
    ) {
      this.modalRenderService
        .renderPipelineStatusMoveConfirmationModal({
          showNurtureCampaignsQuestion: this.pipelineSharedData.showNurtureCampaignsQuestion(
            this.enquirySource,
          ),
          reasonList: this.reasonListView,
        })
        .result.then((result) => {
          if (result.isOk) {
            this.moveLoanStatus(
              result.shouldSendNurtureCampaigns,
              result.selectedReasonId,
            );
            this.origLoanStatusId = this.loanStatusId;
            this.origOrgPipelineStatusId = this.orgPipelineStatusId;
          } else {
            this.loanStatusId = this.origLoanStatusId;
            this.orgPipelineStatusId = this.origOrgPipelineStatusId;
            this.setLoanStatus();
          }
        });
    } else if (
      typelessEqual(this.loanStatus.LoanStatusID, LOAN_STATUS.EXISTING.SETTLED)
    ) {
      this.moveLoanStatus();
      this.moveToSettled();
    } else {
      this.moveLoanStatus();
    }
  }

  getReasonList() {
    this.pipelineService
      .getNotProceedReasons(PIPELINE_TYPES_ID.APPLICATION)
      .then(({ reasonList }) => {
        this.reasonListView = reasonList || null;
      });
  }

  moveLoanStatus(shouldSendNurtureCampaigns, selectedReasonId) {
    this.contactService
      .LoanStatusSet(
        this.loanId,
        this.loanStatusId,
        shouldSendNurtureCampaigns,
        selectedReasonId,
        false,
        this.orgPipelineStatusId,
      )
      .then(() => {
        this.toaster.pop('success', 'Loan Status successfully updated!');
        if (this.loanPipelineCardsId) {
          this.loanAppSharedData.lendingTabPipelineStatusData = {
            PipelineStatusID: this.loanStatusId,
            OrgPipelineStatusID: this.orgPipelineStatusId,
          };
        }
        if (this.uiService.newPipelineTasks) {
          this.automationRuleModelService
            .triggerAutomation({
              loanId: this.loanId,
            })
            .then(({ status }) => {
              status === OK && this.taskDetailService.reloadWorkbenchTaskList();
            });
        }
      });
  }

  /*
   * Description: Get important dates of this loan app
   * */
  importantDateGet() {
    this.loanScenarioService
      .importantDateGet(this.loanScenarioId)
      .then((response) => {
        // we only have business for Next Work Date
        this.nextWorkDate =
          find(response.data, (obj) => {
            obj.Date = moment(obj.Date).format('D MMM YYYY');
            return obj.TypeOfDateID === this.constants.NEXT_WORK_DATE;
          }) || {};
        this.nextWorkDate.isOpen = false;
        this.nextWorkDate.openDate = () => {
          this.nextWorkDate.isOpen = !this.nextWorkDate.isOpen;
        };
      });
  }

  importantDateSet() {
    const data = {
      loanId: this.loanId,
      nextWorkDate: moment(this.nextWorkDate.Date).format('YYYY-MM-DD'),
      description: 'Updated Next Work Date',
    };
    this.loanScenarioService.workbenchNextWorkDate(data).then(() => {
      this.onNwdUpdate();
    });
  }
}

export default workbenchDetailsCtrl;
